/**
 * TabAware — opt-in lifecycle for components/services living inside an admin tab
 * that gets detached/reattached as the user switches tabs.
 *
 * Implement this on a component when it owns subscriptions, observers, or other
 * resources that should pause when the tab is hidden and resume when it returns.
 *
 * AdminRouteReuseStrategy walks the detached `ComponentRef` tree and calls these
 * hooks. Components that don't need them can ignore this interface entirely.
 *
 * Usage:
 *   class MyAdminPage implements TabAware {
 *     onTabDetach() { this.subscription?.unsubscribe(); }
 *     onTabAttach() { this.subscription = this.api.subscribe(...); }
 *   }
 */
export interface TabAware {
  onTabDetach?(): void;
  onTabAttach?(): void;
}

/**
 * Type guard — used by AdminRouteReuseStrategy to safely detect implementers
 * without forcing every component to declare the interface.
 */
export function isTabAware(target: unknown): target is TabAware {
  if (!target || typeof target !== 'object') return false;
  const t = target as Partial<TabAware>;
  return typeof t.onTabDetach === 'function' || typeof t.onTabAttach === 'function';
}
